import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArchiveButton } from "@/components/ArchiveDialog";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { CalendarCheck, Loader2, Plus, FileText, Share2, Trash2, ArrowRight, X } from "lucide-react";
import { useServerView, activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { pl } from "@/components/quality/QrTools";
import { PSummary } from "@/components/print/PrintDoc";

function Card({ children, className = "", ...props }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`} {...props}>{children}</div>; }
function Stat({ label, value, tone }) { return <Card className="p-4"><p className="text-xs text-stone-500">{label}</p><p className={`text-2xl font-semibold mt-0.5 ${tone}`}>{value}</p></Card>; }
const STATUS_LABEL = { draft: "Kladde", final: "Færdigt referat" };
const STATUS_BADGE = { draft: "bg-[#EFE7DC] text-[#8A6D3B] border-[#E3D5C0]", final: "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]" };

const DEFAULT_PARTICIPANTS = ["Jef", "Tom", "Mikael Lautrup", "Steen", "Mathias"];
const DEFAULT_MINUTE_TAKER = "Mathias";
const DEFAULT_GUESTS = ["Mikael K", "Brian"];
const matchUser = (users, target) => {
  const toks = target.toLowerCase().split(/\s+/).filter(Boolean);
  return users.find((u) => { const n = (u.name || "").toLowerCase(); return toks.every((t) => n.includes(t)); });
};

function CreateDialog({ open, onOpenChange, users, onCreated }) {
  const [title, setTitle] = useState("Ledelsesevaluering — opfølgningsmøde");
  const [mdate, setMdate] = useState(null);
  const [location, setLocation] = useState("");
  const [parts, setParts] = useState(() => DEFAULT_PARTICIPANTS.map((t) => matchUser(users, t)).filter(Boolean).map((u) => u.id));
  const [guests, setGuests] = useState(DEFAULT_GUESTS);
  const [guestInput, setGuestInput] = useState("");
  const [minuteTaker, setMinuteTaker] = useState(() => matchUser(users, DEFAULT_MINUTE_TAKER)?.id || null);
  const [saving, setSaving] = useState(false);

  const addGuest = () => { const g = guestInput.trim(); if (g && !guests.includes(g)) setGuests([...guests, g]); setGuestInput(""); };

  const save = async () => {
    if (!title.trim()) { toast.error("Angiv en titel"); return; }
    if (!mdate) { toast.error("Vælg en dato"); return; }
    setSaving(true);
    try {
      const r = await api.post("/le/meetings", { title, date: mdate, location, participant_ids: parts, guests, minute_taker_id: minuteTaker });
      toast.success("Møde oprettet");
      onCreated?.(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="le-meeting-create">
        <DialogHeader><DialogTitle>Opret nyt møde</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><p className="text-xs font-medium text-stone-500 mb-1.5">Titel</p><Input data-testid="le-meeting-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-xs font-medium text-stone-500 mb-1.5">Dato</p><DanishDatePicker value={mdate} onChange={setMdate} testid="le-meeting-date" allowClosed /></div>
            <div><p className="text-xs font-medium text-stone-500 mb-1.5">Mødested</p><Input data-testid="le-meeting-location" value={location} onChange={(e) => setLocation(e.target.value)} className="rounded-xl h-10" placeholder="fx Mødelokale / Teams" /></div>
          </div>
          <div><p className="text-xs font-medium text-stone-500 mb-1.5">Deltagere (brugere)</p><ResponsibleMultiSelect users={users} value={parts} onChange={setParts} testid="le-meeting-participants" className="h-10" /></div>
          <div>
            <p className="text-xs font-medium text-stone-500 mb-1.5">Øvrige deltagere (ikke-brugere)</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {guests.map((g) => (
                <span key={g} data-testid={`le-guest-chip-${g}`} className="inline-flex items-center gap-1 text-sm rounded-full bg-[#F1DEDA] text-[#A83232] px-2.5 py-1">{g}
                  <button onClick={() => setGuests(guests.filter((x) => x !== g))} className="hover:text-[#8f2a2a]"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {guests.length === 0 && <span className="text-sm text-stone-400">Ingen</span>}
            </div>
            <div className="flex gap-2">
              <Input value={guestInput} onChange={(e) => setGuestInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGuest(); } }} placeholder="Tilføj navn…" className="rounded-xl h-9" data-testid="le-guest-input" />
              <Button variant="outline" onClick={addGuest} className="rounded-xl border-[#EBE8E3] h-9" data-testid="le-guest-add">Tilføj</Button>
            </div>
          </div>
          <div><p className="text-xs font-medium text-stone-500 mb-1.5">Referent</p><UserSingleSelect users={users} value={minuteTaker} onChange={setMinuteTaker} testid="le-meeting-minutetaker" className="h-10" placeholder="Vælg referent" /></div>
          <p className="text-xs text-stone-400">Dagsorden bygges automatisk med den faste referatskabelon, månedens LE-punkter fra årshjulet og opfølgning på åbne handlinger. Alt kan tilpasses i kladden.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Annullér</Button>
          <Button data-testid="le-meeting-create-save" onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving ? "Opretter…" : "Opret møde"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LeMeetings() {
  const nav = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const sv = useServerView("/le-view/meetings", { exportPath: "/le-view/meetings/export.xlsx" });
  const visning = sv.getArr("visning");
  const statusFilter = visning.length === 1 ? visning[0] : "all";
  const setStatusFilter = (v) => sv.setParam({ visning: v === "all" ? [] : [v] });

  const load = async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([api.get("/le/meetings"), api.get("/users")]);
      setMeetings(m.data); setUsers(u.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const reload = () => { load(); sv.reload(); };

  const del = async (id) => {
    if (!window.confirm("Arkivér denne mødekladde? Den kan gendannes fra arkivet.")) return;
    try { await api.delete(`/le/meetings/${id}`); toast.success("Arkiveret"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delFinal = async (id) => {
    if (!window.confirm("Arkivér dette færdige referat? Det forsvinder fra oversigten, men kan gendannes fra arkivet.")) return;
    try { await api.delete(`/le/meetings/${id}`); toast.success("Referat arkiveret"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = sv.rows;
  const byDateDesc = (a, b) => (b.date || "").localeCompare(a.date || "");
  const drafts = filtered.filter((m) => m.status === "draft").sort(byDateDesc);
  const finals = filtered.filter((m) => m.status === "final").sort(byDateDesc);
  const finalsByYear = {};
  finals.forEach((m) => { const y = (m.date || "").slice(0, 4) || "—"; (finalsByYear[y] = finalsByYear[y] || []).push(m); });
  const finalYears = Object.keys(finalsByYear).sort().reverse();
  const hasFilter = !!sv.search || statusFilter !== "all" || activeFilters(sv, { aar: "År", maaned: "Måned" }).length > 0;
  const clearFilters = sv.reset;
  const counts = sv.data?.counts || { drafts: meetings.filter((m) => m.status === "draft").length, finals: meetings.filter((m) => m.status === "final").length, total: meetings.length };
  const STATUS_VIEW = { draft: "Kladder", final: "Færdige" };
  const pfilters = [["Visning", STATUS_VIEW[statusFilter] || "Alle"], ...activeFilters(sv, { aar: "År", maaned: "Måned" })];

  const draftRow = (m) => (
    <tr key={m.id} data-testid={`le-meeting-${m.id}`} className="border-t border-[#F0EDE8] hover:bg-[#FBFAF8] cursor-pointer" onClick={() => nav(`/ledelse/moede/${m.id}`)}>
      <td className="px-4 py-3 text-stone-600 whitespace-nowrap align-top">{daDate(m.date)}</td>
      <td className="px-4 py-3 align-top"><p className="font-medium text-[#1A1919]">{m.title}</p><p className="text-xs text-stone-400 mt-0.5">{m.location || "—"} · {(m.participant_names || []).length} deltager(e){m.points_count ? ` · ${m.handled_count || 0}/${m.points_count} punkter behandlet` : ""}</p></td>
      <td className="px-4 py-3 align-top"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[m.status]}`}>{STATUS_LABEL[m.status]}</span></td>
      <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" data-testid={`le-meeting-open-${m.id}`} onClick={() => nav(`/ledelse/moede/${m.id}`)} className="rounded-lg bg-[#A83232] hover:bg-[#8f2a2a]">Fortsæt <ArrowRight className="h-4 w-4 ml-1" /></Button>
          <Button variant="outline" size="sm" data-testid={`le-meeting-delete-${m.id}`} onClick={() => del(m.id)} className="rounded-lg border-[#EBE8E3]"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </td>
    </tr>
  );

  const finalRow = (m) => (
    <tr key={m.id} data-testid={`le-meeting-${m.id}`} className="border-t border-[#F0EDE8] hover:bg-[#FBFAF8] cursor-pointer" onClick={() => nav(`/ledelse/moede/${m.id}`)}>
      <td className="px-4 py-3 text-stone-600 whitespace-nowrap align-top">{daDate(m.date)}</td>
      <td className="px-4 py-3 align-top"><div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-[#1A1919]">{m.title}</span>{m.has_share && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232] inline-flex items-center gap-1"><Share2 className="h-3 w-3" /> Delt</span>}</div><p className="text-xs text-stone-400 mt-0.5">{m.location || "—"} · {(m.participant_names || []).length} deltager(e) · v{m.version}</p></td>
      <td className="px-4 py-3 align-top"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_BADGE[m.status]}`}>{STATUS_LABEL[m.status]}</span></td>
      <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" data-testid={`le-meeting-share-${m.id}`} onClick={() => nav(`/ledelse/moede/${m.id}`, { state: { openShare: true } })} className="rounded-lg border-[#EBE8E3]"><Share2 className="h-4 w-4 mr-1.5" /> Del</Button>
          <Button size="sm" data-testid={`le-meeting-open-final-${m.id}`} onClick={() => nav(`/ledelse/moede/${m.id}`)} className="rounded-lg bg-[#A83232] hover:bg-[#8f2a2a]"><FileText className="h-4 w-4 mr-1.5" /> Se referat</Button>
          <Button variant="outline" size="sm" data-testid={`le-meeting-delete-${m.id}`} onClick={() => delFinal(m.id)} className="rounded-lg border-[#EBE8E3] text-[#A83232] hover:bg-[#F1DEDA]"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6" data-testid="le-meetings-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><CalendarCheck className="h-3.5 w-3.5" /> Ledelse · Ledelsesevaluering</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Møder og referater</h1>
          <p className="text-stone-500 mt-2">Planlagte møder, kladder og færdige referater.</p>
        </div>
        <div className="flex items-center gap-2"><ListActions onExport={sv.exportXlsx} primary={<Button data-testid="le-new-meeting-btn" onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Plus className="h-4 w-4 mr-1.5" /> Opret møde</Button>}><ArchiveButton kind="le_meetings" onRestored={reload} /></ListActions></div>
      </div>

      {!loading && meetings.length > 0 && (<>
        {(() => { const today = new Date().toISOString().slice(0, 10); const up = [...meetings].filter((m) => m.status === "draft").sort((a, b) => (a.date || "").localeCompare(b.date || "")); const next = up.find((m) => (m.date || "") >= today) || up[0]; return next ? (
          <div className="rounded-2xl border border-[#E9C9C3] bg-[#FDF8F6] p-4 flex flex-wrap items-center gap-4" data-testid="le-next-meeting">
            <div className="flex-1 min-w-[220px]">
              <p className="label-caps text-[#A83232]">{(next.date || "") >= today ? "Næste møde" : "Åben kladde"}</p>
              <p className="text-lg font-semibold text-[#1A1919] mt-0.5">{next.title}</p>
              <p className="text-xs text-stone-500 mt-0.5">{daDate(next.date)} · {next.location || "—"} · {(next.participant_names || []).length} deltager(e){(next.month_points || []).length ? ` · Årshjul: ${next.month_points.join(", ")}` : ""}</p>
            </div>
            <div className="text-right"><p className="text-xs text-stone-500">Punkter behandlet</p><p className="text-2xl font-semibold text-[#1A1919]" data-testid="le-next-progress">{next.handled_count ?? 0}/{next.points_count ?? 0}</p></div>
            <Button size="sm" onClick={() => nav(`/ledelse/moede/${next.id}`)} className="rounded-lg bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="le-next-open">Åbn <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        ) : null; })()}
        <div className="grid grid-cols-3 gap-3 max-w-lg">
          <Stat label="Kladder" value={counts.drafts} tone="text-[#8A6D3B]" />
          <Stat label="Færdige referater" value={counts.finals} tone="text-[#5f6b4a]" />
          <Stat label="I alt" value={counts.total} tone="text-[#806B61]" />
        </div>
        <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick hideSaved placeholder="Søg titel, mødested, deltager…" shown={filtered.length} total={sv.data?.total ?? 0} onReset={clearFilters} leadActive={statusFilter !== "all"} match={sv.match} setMatch={sv.setMatch}
          filters={[sv.mkFilter("aar", "År"), sv.mkFilter("maaned", "Måned")]} period={sv.period}
          lead={<div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center">{[["all", "Alle"], ["draft", "Kladder"], ["final", "Færdige"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`le-filter-status-${v}`} onClick={() => setStatusFilter(v)} className={`px-3 h-full rounded-[10px] text-sm font-medium ${statusFilter === v ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{l}</button>))}</div>} />
        <ListPrint kicker="BSM A/S · Ledelsesevaluering" title="Møder og referater" noun="møder" filters={pfilters}
          kpis={[{ label: "Møder i udvalget", value: filtered.length }, { label: "Kladder", value: drafts.length, dark: true }, { label: "Færdige referater", value: finals.length }, { label: "I alt", value: counts.total }]}
          summary={<PSummary>{pl(filtered.length, "møde", "møder")} i udvalget, heraf {pl(drafts.length, "kladde", "kladder")} og {pl(finals.length, "færdigt referat", "færdige referater")}.</PSummary>}
          cols={[{ label: "Dato", key: "dato", w: "22mm", cls: "nowrap" }, { label: "Møde", key: "t2" }, { label: "Mødested", key: "l2", w: "30mm" }, { label: "Status", key: "st", w: "28mm", cls: "nowrap" }, { label: "Deltagere", key: "np", w: "18mm", cls: "nowrap" }, { label: "Punkter", key: "pts", w: "18mm", cls: "nowrap" }]}
          rows={filtered.map((m) => ({ ...m, dato: daDate(m.date), t2: clamp2(m.title), l2: clamp2(m.location || "—"), st: STATUS_LABEL[m.status], np: (m.participant_names || []).length, pts: `${m.handled_count || 0}/${m.points_count || 0}` }))} />
      </>)}

      {loading || sv.data === null ? <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div> : meetings.length === 0 ? (
        <Card className="p-10 text-center" data-testid="le-meetings-empty">
          <CalendarCheck className="h-10 w-10 text-stone-300 mx-auto" />
          <p className="text-stone-500 mt-3">Ingen møder endnu.</p>
          <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] mt-4"><Plus className="h-4 w-4 mr-1.5" /> Opret dit første møde</Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#FBFAF8] text-stone-500 text-xs uppercase tracking-wide">
                <tr><th className="text-left px-4 py-3 font-medium">Dato</th><th className="text-left px-4 py-3 font-medium">Møde</th><th className="text-left px-4 py-3 font-medium">Status</th><th className="text-right px-4 py-3 font-medium">Handling</th></tr>
              </thead>
              <tbody>
                {drafts.length > 0 && (
                  <Fragment>
                    <tr><td colSpan={4} className="px-4 py-2 label-caps text-[#806B61] bg-[#FBFAF8] border-t border-[#EBE8E3]">Kladder · {drafts.length}</td></tr>
                    {drafts.map(draftRow)}
                  </Fragment>
                )}
                {finalYears.map((y) => (
                  <Fragment key={y}>
                    <tr><td colSpan={4} className="px-4 py-2 label-caps text-[#806B61] bg-[#FBFAF8] border-t border-[#EBE8E3]">Færdige referater · {y} · {finalsByYear[y].length}</td></tr>
                    {finalsByYear[y].map(finalRow)}
                  </Fragment>
                ))}
                {drafts.length === 0 && finals.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-stone-400" data-testid="le-meetings-nomatch">Ingen møder matcher dit filter.{hasFilter && <button onClick={clearFilters} className="ml-2 text-[#A83232] hover:underline">Ryd filter</button>}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {createOpen && <CreateDialog open={createOpen} onOpenChange={setCreateOpen} users={users} onCreated={(m) => nav(`/ledelse/moede/${m.id}`)} />}
    </div>
  );
}
